import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { format, isToday, isYesterday } from 'date-fns';
import { enUS, vi as viLocale } from 'date-fns/locale';
import { ChevronDown, ChevronUp, RotateCcw } from 'lucide-react';
import { db } from '../../firebase/config';
import { COLLECTIONS } from '../../firebase/collections';
import { useAuth } from '../../context/AuthContext';
import { useTaskTags } from '../../hooks/useCompanyData';
import TaskTagChips from '../../components/common/TaskTagChips';
import { formatSmartDateTime, formatTime } from '../../utils/time';
import { formatDuration, sanitizeProductionTask } from '../../utils/labour';
import TaskNotes from '../../components/common/TaskNotes';
import './RecentTasksPage.css';

function groupByDate(tasks, t, locale) {
  const map = new Map();
  for (const task of tasks) {
    const d = task.submittedAt?.toDate?.() ?? null;
    let key, label;
    if (!d) {
      key = '__no-date';
      label = t('common.earlier');
    } else if (isToday(d)) {
      key = '__today';
      label = t('common.today');
    } else if (isYesterday(d)) {
      key = '__yesterday';
      label = t('common.yesterday');
    } else {
      key = format(d, 'yyyy-MM-dd');
      label = format(d, 'EEE, MMM d', { locale });
    }
    if (!map.has(key)) map.set(key, { label, tasks: [] });
    map.get(key).tasks.push(task);
  }
  return Array.from(map.values());
}

function TaskCard({ task, expanded, onToggle, onStartAgain, tagsById }) {
  const { t } = useTranslation();
  const taskName = task.isCustomTaskName ? task.customTaskName : task.taskName;

  const assignmentLabel = task.assignmentType === 'team'
    ? `${t('production.team')} × ${task.teamSize}`
    : task.employeeNames?.join(', ') || t('production.individual');

  return (
    <div className={`recent-task-card ${expanded ? 'is-expanded' : ''}`}>
      {/* Always-visible header row */}
      <button className="recent-task-main" onClick={onToggle}>
        <div className="recent-task-top">
          <div className="recent-task-name">
            {taskName}
            {task.isCustomTaskName && (
              <span className="badge badge-custom" style={{ marginLeft: 6, fontSize: 10 }}>
                {t('production.customTaskIndicator')}
              </span>
            )}
          </div>
          <span className={`badge badge-${task.taskType}`}>{t(`categories.${task.taskType}`)}</span>
        </div>

        <TaskTagChips task={task} tagsById={tagsById} maxVisible={2} className="recent-task-tags" />

        <div className="recent-task-meta">
          {task.roomName && <span className="recent-task-meta-item">{task.roomName}</span>}
          {task.lotNumber && (
            <span className="recent-task-meta-item">{t('productionMeta.lotPrefix', { number: task.lotNumber })}</span>
          )}
          {task.quantityCompleted != null && (
            <span className="recent-task-meta-item">
              {task.quantityCompleted}{task.unitType ? ` ${task.unitType}` : ''}
            </span>
          )}
        </div>

        <div className="recent-task-footer">
          <span className="recent-task-time">
            {task.submittedAt ? formatSmartDateTime(task.submittedAt.toDate?.() ?? task.submittedAt) : '—'}
          </span>
          <div className="recent-task-footer-right">
            {task.durationMinutes != null && (
              <span className="recent-task-duration">{formatDuration(task.durationMinutes)}</span>
            )}
            {expanded ? <ChevronUp size={15} className="recent-task-chevron" /> : <ChevronDown size={15} className="recent-task-chevron" />}
          </div>
        </div>
      </button>

      {/* Expanded detail panel */}
      {expanded && (
        <div className="recent-task-detail">
          <div className="recent-task-detail-grid">
            <div className="recent-task-detail-row">
              <span className="recent-task-detail-label">{t('production.taskAssignment')}</span>
              <span className="recent-task-detail-value">{assignmentLabel}</span>
            </div>
            {task.startTime && (
              <div className="recent-task-detail-row">
                <span className="recent-task-detail-label">{t('production.startedAt')}</span>
                <span className="recent-task-detail-value">
                  {formatTime(task.startTime?.toDate?.() ?? task.startTime)}
                </span>
              </div>
            )}
            {task.endTime && (
              <div className="recent-task-detail-row">
                <span className="recent-task-detail-label">{t('production.endedAt')}</span>
                <span className="recent-task-detail-value">
                  {formatTime(task.endTime?.toDate?.() ?? task.endTime)}
                </span>
              </div>
            )}
            {task.productName && (
              <div className="recent-task-detail-row">
                <span className="recent-task-detail-label">{t('production.productName')}</span>
                <span className="recent-task-detail-value">{task.productName}</span>
              </div>
            )}
            {task.sku && (
              <div className="recent-task-detail-row">
                <span className="recent-task-detail-label">{t('production.sku')}</span>
                <span className="recent-task-detail-value">{task.sku}</span>
              </div>
            )}
            {task.downtimeMinutes > 0 && (
              <div className="recent-task-detail-row">
                <span className="recent-task-detail-label">{t('production.downtimeMinutes')}</span>
                <span className="recent-task-detail-value">
                  {task.downtimeMinutes} {t('common.minutes')}
                  {task.downtimeReason ? ` — ${task.downtimeReason}` : ''}
                </span>
              </div>
            )}
            {task.notes && (
              <div className="recent-task-detail-row recent-task-detail-row--notes">
                <span className="recent-task-detail-label">{t('production.notes')}</span>
                <span className="recent-task-detail-value">{task.notes}</span>
              </div>
            )}
          </div>

          <TaskNotes
            taskId={task.id}
            taskCreatedBy={task.createdBy}
            task={task}
            collapsible
          />

          <button className="btn btn-outline btn-sm recent-task-start-again" onClick={onStartAgain}>
            <RotateCcw size={14} />
            {t('production.startAgain')}
          </button>
        </div>
      )}
    </div>
  );
}

export default function RecentTasksPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, companyId } = useAuth();
  const { tags } = useTaskTags();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    if (!user || !companyId) { setLoading(false); return; }
    const q = query(
      collection(db, COLLECTIONS.PRODUCTION_TASKS),
      where('companyId', '==', companyId),
      where('createdBy', '==', user.uid),
      where('status', '==', 'completed'),
      where('archived', '==', false),
      orderBy('submittedAt', 'desc'),
      limit(30)
    );
    return onSnapshot(q, (snap) => {
      setTasks(snap.docs.map((d) => sanitizeProductionTask({ id: d.id, ...d.data() })));
      setLoading(false);
    });
  }, [user, companyId]);

  const tagsById = useMemo(() => new Map(tags.map((tg) => [tg.id, tg])), [tags]);

  const handleStartAgain = (task) => {
    navigate('/production/start-task', {
      state: {
        prefill: {
          taskMode: task.isCustomTaskName ? 'custom' : 'preset',
          taskCategoryId: task.taskCategoryId ?? '',
          taskCategoryName: task.taskCategoryName ?? '',
          taskType: task.taskType ?? '',
          taskName: task.taskName ?? '',
          isCustomTaskName: task.isCustomTaskName ?? false,
          customTaskName: task.customTaskName ?? '',
          customTaskType: task.isCustomTaskName ? (task.taskType ?? '') : '',
        },
      },
    });
  };

  if (loading) return <div className="page-loading"><div className="spinner spinner-lg" /></div>;

  if (tasks.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">📋</div>
        <h3>{t('production.noRecentTasks')}</h3>
        <p>{t('production.noRecentTasksHint')}</p>
      </div>
    );
  }

  const groups = groupByDate(tasks, t, i18n.language === 'vi' ? viLocale : enUS);

  return (
    <div className="recent-tasks-page">
      <div className="recent-tasks-header">
        <h2 className="font-semibold">{t('production.recentTasks')}</h2>
        <span className="recent-tasks-count">{tasks.length}</span>
      </div>
      {groups.map((group) => (
        <div key={group.label} className="recent-group">
          <div className="recent-group-label">{group.label}</div>
          <div className="recent-list">
            {group.tasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                expanded={expandedId === task.id}
                onToggle={() => setExpandedId(expandedId === task.id ? null : task.id)}
                onStartAgain={() => handleStartAgain(task)}
                tagsById={tagsById}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
